import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppProvider } from './context/AppContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { OfflineProvider } from './context/OfflineContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Layout from './components/layout/Layout';
import LandingPage from './pages/LandingPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Login from './pages/Login';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import Dashboard from './pages/Dashboard';
import CleaningOrdersDesktop from './pages/CleaningOrders/CleaningOrdersDesktop';
import CleaningOrdersMobile from './pages/CleaningOrders/CleaningOrdersMobile';
import UserManagement from './pages/UserManagement/UserManagement';
import RoomManagement from './pages/RoomManagement/RoomManagement';
import CommonSpacesManagement from './pages/CommonSpaces/CommonSpacesManagement';
import IncidentsManagement from './pages/Incidents/IncidentsManagement';
import EstablishmentManagement from './pages/EstablishmentManagement/EstablishmentManagement';
import EstablishmentSettings from './pages/EstablishmentManagement/EstablishmentSettings';
import SubscriptionSettings from './pages/EstablishmentManagement/SubscriptionSettings';
import SubscriptionPlansManagement from './pages/Admin/SubscriptionPlansManagement';
import PlatformAdminDashboard from './pages/Admin/PlatformAdminDashboard';
import ScheduleManagement from './pages/Schedules/ScheduleManagement';
import TimeEntriesManagement from './pages/TimeEntries/TimeEntriesManagement';
import MyTimeSheet from './pages/TimeEntries/MyTimeSheet';
import AdminReports from './pages/Admin/AdminReports';
import EstablishmentDetail from './pages/Admin/EstablishmentDetail';
import DemoRequestsManagement from './pages/Admin/DemoRequestsManagement';
import NotificationContainer from './components/common/NotificationContainer';
import InstallPWA from './components/common/InstallPWA';
import OfflineIndicator from './components/common/OfflineIndicator';
import MaintenanceScreen from './pages/MaintenanceScreen';
import { USER_ROLES } from './utils/constants';
import useGoogleAnalytics from './hooks/useGoogleAnalytics';

function App() {
  const [isServerDown, setIsServerDown] = useState(false);

  useEffect(() => {
    const handleServerMaintenance = () => {
      setIsServerDown(true);
    };

    window.addEventListener('server-maintenance', handleServerMaintenance);

    return () => {
      window.removeEventListener('server-maintenance', handleServerMaintenance);
    };
  }, []);

  // Show maintenance screen if server is down
  if (isServerDown) {
    return <MaintenanceScreen />;
  }

  return (
    <AuthProvider>
      <WebSocketProvider>
        <AppProvider>
          <OfflineProvider>
            <Router>
              <AnalyticsTracker />
              <div className="App">
              <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/login" element={<Login />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/payment-cancel" element={<PaymentCancel />} />

              {/* Protected routes */}
              <Route path="/dashboard-redirect" element={
                <ProtectedRoute>
                  <Layout>
                    <HomeRedirect />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Admin Dashboard - Platform Admin only */}
              <Route path="/admin/dashboard" element={
                <ProtectedRoute allowedRoles={[USER_ROLES.PLATFORM_ADMIN]}>
                  <Layout>
                    <PlatformAdminDashboard />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Establishment Detail - Platform Admin only */}
              <Route path="/admin/establishments/:id" element={
                <ProtectedRoute allowedRoles={[USER_ROLES.PLATFORM_ADMIN]}>
                  <Layout>
                    <EstablishmentDetail />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Demo Requests Management - Platform Admin only */}
              <Route path="/admin/demo-requests" element={
                <ProtectedRoute allowedRoles={[USER_ROLES.PLATFORM_ADMIN]}>
                  <Layout>
                    <DemoRequestsManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/dashboard" element={
                <ProtectedRoute allowedRoles={[
                  USER_ROLES.PLATFORM_ADMIN,
                  USER_ROLES.ESTABLISHMENT_ADMIN,
                  USER_ROLES.MANAGER,
                  USER_ROLES.RECEPTIONIST
                ]}>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Cleaning Orders - Desktop view for admins, managers and receptionists */}
              <Route path="/orders/desktop" element={
                <ProtectedRoute allowedRoles={[
                  USER_ROLES.PLATFORM_ADMIN,
                  USER_ROLES.ESTABLISHMENT_ADMIN,
                  USER_ROLES.MANAGER,
                  USER_ROLES.RECEPTIONIST
                ]}>
                  <Layout>
                    <CleaningOrdersDesktop />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Cleaning Orders - Mobile view for cleaning staff */}
              <Route path="/orders/mobile" element={
                <ProtectedRoute allowedRoles={[USER_ROLES.CLEANING_STAFF]}>
                  <Layout>
                    <CleaningOrdersMobile />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Redirect /orders to appropriate view based on user role */}
              <Route path="/orders" element={
                <ProtectedRoute>
                  <Layout>
                    <OrdersRedirect />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* User Management */}
              <Route path="/users" element={
                <ProtectedRoute allowedRoles={[
                  USER_ROLES.PLATFORM_ADMIN,
                  USER_ROLES.SALES,
                  USER_ROLES.ESTABLISHMENT_ADMIN,
                  USER_ROLES.MANAGER
                ]}>
                  <Layout>
                    <UserManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Configuration (Rooms, Common Spaces, Settings) */}
              <Route path="/configuration" element={
                <ProtectedRoute allowedRoles={[
                  USER_ROLES.PLATFORM_ADMIN,
                  USER_ROLES.ESTABLISHMENT_ADMIN,
                  USER_ROLES.MANAGER
                ]}>
                  <Layout>
                    <RoomManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Common Spaces Management */}
              <Route path="/common-spaces" element={
                <ProtectedRoute allowedRoles={[
                  USER_ROLES.PLATFORM_ADMIN,
                  USER_ROLES.ESTABLISHMENT_ADMIN,
                  USER_ROLES.MANAGER,
                  USER_ROLES.RECEPTIONIST
                ]}>
                  <Layout>
                    <CommonSpacesManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Incidents Management */}
              <Route path="/incidents" element={
                <ProtectedRoute allowedRoles={[
                  USER_ROLES.PLATFORM_ADMIN,
                  USER_ROLES.ESTABLISHMENT_ADMIN,
                  USER_ROLES.MANAGER,
                  USER_ROLES.RECEPTIONIST
                ]}>
                  <Layout>
                    <IncidentsManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Establishment Management - Platform Admin and Sales */}
              <Route path="/establishments" element={
                <ProtectedRoute allowedRoles={[USER_ROLES.PLATFORM_ADMIN, USER_ROLES.SALES]}>
                  <Layout>
                    <EstablishmentManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Admin Establishments List - Platform Admin and Sales (same as /establishments for admin purposes) */}
              <Route path="/admin/establishments" element={
                <ProtectedRoute allowedRoles={[USER_ROLES.PLATFORM_ADMIN, USER_ROLES.SALES]}>
                  <Layout>
                    <EstablishmentManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Establishment Settings - Establishment Admin and Manager */}
              <Route path="/establishment-settings" element={
                <ProtectedRoute allowedRoles={[
                  USER_ROLES.ESTABLISHMENT_ADMIN,
                  USER_ROLES.MANAGER
                ]}>
                  <Layout>
                    <EstablishmentSettings />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Subscription Settings - Establishment Admin only */}
              <Route path="/subscription" element={
                <ProtectedRoute allowedRoles={[USER_ROLES.ESTABLISHMENT_ADMIN]}>
                  <Layout>
                    <SubscriptionSettings />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Subscription Plans Management - Platform Admin and Sales */}
              <Route path="/subscription-plans" element={
                <ProtectedRoute allowedRoles={[USER_ROLES.PLATFORM_ADMIN, USER_ROLES.SALES]}>
                  <Layout>
                    <SubscriptionPlansManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Schedule Management */}
              <Route path="/schedules" element={
                <ProtectedRoute allowedRoles={[
                  USER_ROLES.ESTABLISHMENT_ADMIN,
                  USER_ROLES.MANAGER,
                  USER_ROLES.RECEPTIONIST
                ]}>
                  <Layout>
                    <ScheduleManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Time Entries Management - Establishment Admin and Manager */}
              <Route path="/time-entries" element={
                <ProtectedRoute allowedRoles={[
                  USER_ROLES.ESTABLISHMENT_ADMIN,
                  USER_ROLES.MANAGER
                ]}>
                  <Layout>
                    <TimeEntriesManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* My Time Sheet - Combined schedule and time entries calendar */}
              <Route path="/my-timesheet" element={
                <ProtectedRoute>
                  <Layout>
                    <MyTimeSheet />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Reports - Establishment Admin and Manager */}
              <Route path="/reports" element={
                <ProtectedRoute allowedRoles={[
                  USER_ROLES.ESTABLISHMENT_ADMIN,
                  USER_ROLES.MANAGER
                ]}>
                  <Layout>
                    <AdminReports />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Catch all - redirect to dashboard */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>

              {/* Global notifications */}
              <NotificationContainer />

              {/* Offline/Sync Indicator */}
              <OfflineIndicator />

              {/* PWA Install Prompt */}
              <InstallPWA />
            </div>
          </Router>
        </OfflineProvider>
      </AppProvider>
      </WebSocketProvider>
    </AuthProvider>
  );
}

// GA4 route tracking — must be inside <Router>
const AnalyticsTracker = () => {
  useGoogleAnalytics();
  return null;
};

// Component to redirect to appropriate home view based on user role
const HomeRedirect = () => {
  const { user } = useAuth();

  if (user?.role === USER_ROLES.CLEANING_STAFF) {
    return <Navigate to="/orders/mobile" replace />;
  } else if (user?.role === USER_ROLES.PLATFORM_ADMIN) {
    return <Navigate to="/admin/dashboard" replace />;
  } else if (user?.role === USER_ROLES.SALES) {
    return <Navigate to="/admin/establishments" replace />;
  } else {
    return <Navigate to="/dashboard" replace />;
  }
};

// Component to redirect to appropriate orders view based on user role
const OrdersRedirect = () => {
  const { user } = useAuth();

  if (user?.role === USER_ROLES.CLEANING_STAFF) {
    return <Navigate to="/orders/mobile" replace />;
  } else {
    return <Navigate to="/orders/desktop" replace />;
  }
};

export default App;